/**
 * The editor's sections: the saved ones in reading order (each with Move up and Move down, which save the new order
 * at once), then the kind's suggestions not written yet, then **Add a custom section**. Each card saves on its own.
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';

import { isStaleRevision, useAssetTemplate, useOrderSections } from '@/api/queries';
import type { AssetOut } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { LoadFailure } from '@/ui/StateCard';

import { SectionEditorCard } from './SectionEditorCard';
import { moveKey, planSections, type PlannedSection } from './sectionPlan';

let nextDraftId = 0;

function newCustom(): PlannedSection {
  nextDraftId += 1;
  return { key: `draft-${nextDraftId}`, title: '', state: 'custom', section: null, internal: false };
}

export function SectionsEditor({ asset, onStale }: { asset: AssetOut; onStale: () => void }) {
  const { can } = useSession();
  const template = useAssetTemplate(asset.kind);
  const order = useOrderSections(asset.id);
  const [drafts, setDrafts] = useState<PlannedSection[]>([]);
  const [warnings, setWarnings] = useState<Record<string, string[]>>({});
  const { saved, suggested } = planSections(asset.sections, template.data?.sections ?? [], can('asset.view.internal'));
  const keys = saved.map((s) => s.key);

  const move = (index: number, delta: -1 | 1) =>
    order.mutate(
      { keys: moveKey(keys, index, delta), revision: asset.revision },
      { onError: (error) => isStaleRevision(error) && onStale() },
    );
  const common = {
    asset,
    onStale,
    onWarnings: (key: string, list: string[]) => setWarnings((w) => ({ ...w, [key]: list })),
  };

  return (
    <section className="asset-editor-sections" aria-labelledby="editor-sections-title">
      <h2 id="editor-sections-title">Sections</h2>
      {template.error && <LoadFailure error={template.error} onRetry={() => void template.refetch()} />}
      <p className="sr-only" aria-live="polite">{order.isPending ? 'Saving the section order' : ''}</p>
      {saved.map((p, i) => (
        <SectionEditorCard
          key={p.key}
          {...common}
          planned={p}
          warnings={warnings[p.key] ?? []}
          onMoveUp={i > 0 && !order.isPending ? () => move(i, -1) : undefined}
          onMoveDown={i < saved.length - 1 && !order.isPending ? () => move(i, 1) : undefined}
        />
      ))}
      {suggested.length > 0 && <h3 className="asset-editor-subhead">Suggested for this kind</h3>}
      {suggested.map((p) => (
        <SectionEditorCard key={p.key} {...common} planned={p} warnings={warnings[p.key] ?? []} />
      ))}
      {drafts.map((p) => (
        <SectionEditorCard
          key={p.key}
          {...common}
          planned={p}
          warnings={[]}
          onDone={() => setDrafts((list) => list.filter((d) => d.key !== p.key))}
        />
      ))}
      <button type="button" className="btn btn-secondary" onClick={() => setDrafts((list) => [...list, newCustom()])}>
        <Plus aria-hidden="true" /> Add a custom section
      </button>
    </section>
  );
}
